// Shared password field for every auth screen: a show/hide toggle (eye
// icon) so users can verify what they've typed, matching a pattern common
// enough elsewhere that its absence was noticed (2026-08-22). Not part of
// the design kit — built directly, since it's a well-understood, low-risk
// UI pattern that doesn't need a Lovable pass.
import { useState } from "react";
import type { InputHTMLAttributes } from "react";
import { Eye, EyeOff, KeyRound, type LucideIcon } from "lucide-react";

import { authFieldClassName } from "@/components/snapdraw/auth-layout";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export function PasswordInput({
  invalid,
  icon: Icon = KeyRound,
  className,
  "aria-label": ariaLabel,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; icon?: LucideIcon }) {
  const t = useT();
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative flex items-center">
      <Icon className="pointer-events-none absolute left-4 h-4 w-4 text-arc" />
      <input
        {...props}
        type={visible ? "text" : "password"}
        // Backlog A11Y-1: every call site relied on a placeholder alone
        // (in the worst case, LoginPage's own placeholder is just literal
        // dots) — a placeholder disappears once typing starts and isn't a
        // reliable accessible name for assistive technology. Defaults to
        // a generic label; a caller with more than one password field on
        // the same screen (e.g. ResetPasswordPage's "new" vs. "confirm")
        // passes its own aria-label to stay distinguishable.
        aria-label={ariaLabel ?? t("auth.password.label")}
        className={cn(authFieldClassName(invalid), "pr-11", className)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t("auth.password.hide") : t("auth.password.show")}
        className="absolute right-4 text-muted-foreground hover:text-arc"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
